import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, FormControlLabel } from '@material-ui/core';
import { Button, Switch, AlertBanner, Loader } from '@saviynt/design-system';
import { saveConfig, saveAiAgentConfig, fetchSourceStatus } from '../../redux/migrationThunks';

function GlobalConfiguration({ classes, sourceID }) {
  const dispatch = useDispatch();
  const { sourceStatus } = useSelector((state) => state.migration ?? {});
  const [enabled, setEnabled] = useState(sourceStatus?.dataMigrationEnabled ?? false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ visible: false, type: 'Success', message: '' });
  const [aiAgentEnabled, setAiAgentEnabled] = useState(sourceStatus?.aiAgentServiceEnabled ?? false);
  const [savingAiAgent, setSavingAiAgent] = useState(false);

  const handleSave = () => {
    setSaving(true);
    dispatch(saveConfig({ configValue: String(enabled) }))
      .unwrap()
      .then(() => {
        if (sourceID) dispatch(fetchSourceStatus(sourceID));
        setBanner({ visible: true, type: 'Success', message: 'Configuration saved successfully.' });
      })
      .catch((err) => {
        setBanner({ visible: true, type: 'Critical', message: err?.message || 'Failed to save configuration.' });
      })
      .finally(() => setSaving(false));
  };

  const handleSaveAiAgent = () => {
    setSavingAiAgent(true);
    dispatch(saveAiAgentConfig({ configValue: String(aiAgentEnabled) }))
      .unwrap()
      .then(() => {
        setBanner({ visible: true, type: 'Success', message: 'AI Agent configuration saved successfully.' });
      })
      .catch((err) => {
        setBanner({ visible: true, type: 'Critical', message: err?.message || 'Failed to save AI Agent configuration.' });
      })
      .finally(() => setSavingAiAgent(false));
  };

  return (
    <div className={classes.globalConfigWrapper}>
      {banner.visible && (
        <AlertBanner
          colorTheme={banner.type}
          title={banner.type === 'Success' ? 'Success' : 'Error'}
          description={banner.message}
          isVisible={banner.visible}
          isFloating
          position={{ top: '16px', right: '16px' }}
          onCancel={() => setBanner((prev) => ({ ...prev, visible: false }))}
          shouldAutoDismiss
          autoDismissDelay={5000}
          isCloseButtonVisible
          className={classes.alertBanner}
        />
      )}
      {saving && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      {savingAiAgent && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      <Typography className={classes.configSectionTitle}>Data Migration Settings</Typography>
      <Typography className={classes.configSectionDesc}>
        Enable or disable data migration for this source.
      </Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>Enable Data Migration</Typography>
            <Typography className={classes.configRowHint}>
              Allow data migration for this source system
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <FormControlLabel
              control={
                <Switch
                  isChecked={enabled}
                  onChange={() => setEnabled(!enabled)}
                  ariaLabel="Toggle option for data migration tab"
                />
              }
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
      <hr className={classes.configSectionDivider} />
      <Typography className={classes.configSectionTitle}>Use AI Agent Service</Typography>
      <Typography className={classes.configSectionDesc}>
        Enable or disable AI Agent Service for this source.
      </Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>Enable AI Agent Service</Typography>
            <Typography className={classes.configRowHint}>
              Allow AI Agent Service for this source system
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <FormControlLabel
              control={
                <Switch
                  isChecked={aiAgentEnabled}
                  onChange={() => setAiAgentEnabled(!aiAgentEnabled)}
                  ariaLabel="Toggle option for AI agent service"
                />
              }
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSaveAiAgent}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

GlobalConfiguration.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  sourceID: PropTypes.string,
};

GlobalConfiguration.defaultProps = {
  sourceID: null,
};

export default GlobalConfiguration;
